<template>
  <NcrmRecordLayout :left-lg-cols="3" :main-lg-cols-both="7" :right-lg-cols="2">
    <template #header>
      <NcrmRecordHeader
        :label="$t('ncrm.contacts.details.recordLabel')"
        :title="recordTitle"
        :subtitle="recordSubtitle"
        :chips="headerChips"
        :kpis="headerKpis"
        :clickable-kpi-keys="headerClickableKpiKeys"
        show-back-button
        :back-aria-label="$t('app.actions.back')"
        @back-click="goBackToContacts"
        @kpi-click="handleHeaderKpiClick"
      >
        <template #actions>
          <NcrmContactHeaderActions
            entity-type="person"
            :entity-id="recordId"
            ai-profile-key="quick_assist"
            :ai-label="$t('ncrm.ai.actions.personAssist')"
            :disabled="!record" :recipient-email="record?.email_primary || ''" :recipient-name="recordTitle"
            @error="showSnackbar($event, 'error')" @notify="onMailjetNotify" @email-sent="handleCompanyEmailSent"
            @add-activity="openAddActivity"
            @edit="openEditDialog"
            @delete="openDeleteDialog"
          />
        </template>
      </NcrmRecordHeader>
    </template>
    <template #left>
      <NcrmRecordPropertiesCard
        :title="$t('ncrm.contacts.details.sections.personProperties')"
        :loading="loading"
        :sections="personPropertySectionsForDisplay"
        :skeleton-groups="propertySkeletonGroups"
        :empty-label="$t('ncrm.contacts.details.propertyEmpty.notSet')"
        :empty-sections-label="$t('ncrm.contacts.details.propertySections.empty')"
        :copy-aria-label="$t('ncrm.contacts.details.propertyInteractions.copy')"
        show-settings-button
        :settings-disabled="!record"
        :settings-aria-label="$t('ncrm.contacts.details.propertyLayout.actions.open')"
        :resolve-row-link="resolvePropertyRowLink"
        :privacy-subject="{ type: 'person', id: recordId }"
        :resolve-row-tooltip="resolveTimestampTooltip"
        :can-copy-row="canCopyPropertyRow"
        @settings-click="openPropertyLayoutDrawer"
        @copy-row="copyPropertyRowValue"
        @empty-action="handlePropertySectionEmptyAction"
      />
    </template>
    <template #main>
      <v-card class="pa-4">
        <div class="text-subtitle-1 ncrm-record-title">
          {{ $t("ncrm.contacts.details.sections.mainWorkspace") }}
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />
        <div v-show="!loading" class="mt-3">
          <v-tabs v-model="mainTab" color="primary" density="comfortable" show-arrows class="ncrm-main-workspace-tabs">
            <v-tab value="activity">{{ $t("ncrm.contacts.details.tabs.activity") }}</v-tab>
            <v-tab value="overview">{{ $t("ncrm.contacts.details.tabs.overview") }}</v-tab>
            <v-tab value="mailjet">{{ $t("ncrm.integrations.mailjet.activity.tab") }}</v-tab>
            <v-tab value="data-protection">{{ $t("ncrm.contacts.dataProtection.header") }}</v-tab>
            <v-tab value="history">{{ $t("ncrm.contacts.details.tabs.history") }}</v-tab>
          </v-tabs>
          <v-window v-model="mainTab" class="ncrm-main-workspace-window" :touch="false">
            <v-window-item value="activity">
              <NcrmActivityTimeline
                v-if="mainTab === 'activity'" :key="`person-activity-${recordId}-${activityRefreshKey}`"
                entity-type="person"
                :entity-id="recordId" scope="customer"
                :composer-open="activityComposerOpen"
                @update:composer-open="activityComposerOpen = $event"
              />
            </v-window-item>
            <v-window-item value="overview">
              <NcrmPrivacyOverviewStatus subject-type="person" :subject-id="recordId" :active="mainTab === 'overview'" @activate="mainTab = 'data-protection'" />
              <div v-if="hasOverviewBoardItems" class="ncrm-overview-board-sections">
                <section>
                  <div class="text-caption text-medium-emphasis mb-2">
                    {{ $t("ncrm.contacts.details.overview.leadBoardsTitle") }}
                  </div>
                  <v-list
                    v-if="leadBoardsOverviewItems.length"
                    lines="two"
                    density="comfortable"
                    class="ncrm-overview-board-list pa-0"
                  >
                    <v-list-item
                      v-for="item in leadBoardsOverviewItems"
                      :key="`person-overview-lead-${item.placementId || item.boardId || item.stageId}`"
                      class="ncrm-overview-board-list__item"
                    >
                      <v-list-item-title>{{ formatOverviewBoardName(item) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ formatOverviewLeadSubtitle(item) }}</v-list-item-subtitle>
                      <template #append>
                        <v-chip size="x-small" :color="resolveOverviewStatusColor('lead', item.status)">
                          {{ formatLeadOverviewStatus(item.status) }}
                        </v-chip>
                      </template>
                    </v-list-item>
                  </v-list>
                  <div v-else class="text-body-2 text-medium-emphasis">
                    {{ $t("ncrm.contacts.details.overview.emptyLeadBoards") }}
                  </div>
                </section>
                <section>
                  <div class="text-caption text-medium-emphasis mb-2">
                    {{ $t("ncrm.contacts.details.overview.dealBoardsTitle") }}
                  </div>
                  <v-list
                    v-if="dealBoardsOverviewItems.length"
                    lines="two"
                    density="comfortable"
                    class="ncrm-overview-board-list pa-0"
                  >
                    <v-list-item
                      v-for="item in dealBoardsOverviewItems"
                      :key="`person-overview-deal-${item.placementId || item.boardId || item.stageId}`"
                      class="ncrm-overview-board-list__item"
                    >
                      <v-list-item-title>{{ formatOverviewBoardName(item) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ formatOverviewDealSubtitle(item) }}</v-list-item-subtitle>
                      <template #append>
                        <v-chip size="x-small" :color="resolveOverviewStatusColor('deal', item.status)">
                          {{ formatDealOverviewStatus(item.status) }}
                        </v-chip>
                      </template>
                    </v-list-item>
                  </v-list>
                  <div v-else class="text-body-2 text-medium-emphasis">
                    {{ $t("ncrm.contacts.details.overview.emptyDealBoards") }}
                  </div>
                </section>
              </div>
              <div v-else class="text-body-2 text-medium-emphasis">
                {{ $t("ncrm.contacts.details.overview.empty") }}
              </div>
            </v-window-item>
            <v-window-item value="mailjet">
              <NcrmMailjetActivityPanel v-if="mainTab === 'mailjet'" entity-type="person"
                :entity-id="recordId" @notify="onMailjetNotify" />
            </v-window-item>
            <v-window-item value="data-protection" eager>
              <NcrmPrivacyRecordTab v-if="privacyTabVisited" subject-type="person" :subject-id="recordId" /></v-window-item>
            <v-window-item value="history">
              <NcrmRecordHistoryTimeline
                v-if="mainTab === 'history'"
                entity-type="person"
                :entity-id="recordId"
              />
            </v-window-item>
          </v-window>
        </div>
      </v-card>
    </template>
    <template #right>
      <v-card class="pa-4">
        <div class="text-subtitle-1 ncrm-record-title">
          {{ $t("ncrm.contacts.details.sections.context") }}
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />
        <div v-else class="mt-3">
          <div class="text-caption text-medium-emphasis ncrm-context-section-title">
            {{ $t("ncrm.contacts.details.person.companyLink.title") }}
          </div>
          <div class="text-body-2 ncrm-details-list__value mt-1">
            {{ record?.company_name || emptyValueLabel }}
          </div>
          <div class="d-flex flex-wrap ga-2 mt-3">
            <v-btn
              size="small"
              color="primary"
              variant="outlined"
              :disabled="!record"
              @click="openCompanyLinkDrawer"
            >
              <v-icon start size="16">mdi-domain</v-icon>
              {{
                record?.ncrm_company_id
                  ? $t("ncrm.contacts.details.person.companyLink.change")
                  : $t("ncrm.contacts.details.person.companyLink.link")
              }}
            </v-btn>
            <v-btn
              v-if="record?.ncrm_company_id"
              size="small"
              color="error"
              variant="text"
              :loading="companyLinkSaving"
              @click="clearCompanyLink"
            >
              {{ $t("ncrm.contacts.details.person.companyLink.clear") }}
            </v-btn>
          </div>
          <div class="ncrm-context-section-sep" />
          <div class="text-caption text-medium-emphasis ncrm-context-section-title mb-1">
            {{ $t("ncrm.contacts.details.person.fields.customFields") }}
          </div>
          <div v-if="customFieldsForDisplay.length" class="ncrm-details-list">
            <div
              v-for="entry in customFieldsForDisplay"
              :key="`context-custom-field-${entry.field_definition_id}`"
              class="ncrm-details-list__item"
            >
              <div class="text-caption text-medium-emphasis">{{ entry.label }}</div>
              <div class="text-body-2 ncrm-details-list__value">
                {{ entry.value_text || emptyValueLabel }}
              </div>
            </div>
          </div>
          <div v-else class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.contacts.details.person.emptyCustomFields") }}
          </div>
        </div>
      </v-card>
    </template>
  </NcrmRecordLayout>
  <NcrmRecordEditDialog
    v-model="editDialog"
    :title="$t('ncrm.contacts.details.person.dialogs.editTitle')"
    :subtitle="recordTitle"
    :loading="editLoading"
    :extra-action-label="$t('ncrm.contacts.details.dialogs.completeAllValues')"
    :extra-action-disabled="editShowAllFields"
    @extra-action="expandEditFormFields"
    @save="submitEdit"
  >
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.first_name"
          :label="$t('ncrm.contacts.details.person.fields.firstName')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.last_name"
          :label="$t('ncrm.contacts.details.person.fields.lastName')"
          required
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.position"
          :label="$t('ncrm.contacts.details.person.fields.position')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.department"
          :label="$t('ncrm.contacts.details.person.fields.department')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="editForm.business_is_active"
          :items="businessActiveOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.contacts.details.businessStatus.fieldLabel')"
          :menu-props="selectMenuProps"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.email_primary"
          :label="$t('ncrm.contacts.details.person.fields.emailPrimary')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.phone_primary"
          :label="$t('ncrm.contacts.details.person.fields.phonePrimary')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.source"
          :label="$t('ncrm.contacts.details.person.fields.source')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.country"
          :label="$t('ncrm.contacts.details.person.fields.country')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          :model-value="record?.company_name || emptyValueLabel"
          :label="$t('ncrm.contacts.details.person.fields.companyName')"
          readonly
        />
      </v-col>
      <template v-if="editShowAllFields">
        <v-col cols="12">
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.dialogs.additionalFields") }}
          </div>
        </v-col>
        <v-col
          v-for="field in personAdvancedEditFields"
          :key="`person-edit-field-${field.key}`"
          cols="12"
          md="6"
        >
          <component
            :is="field.component"
            v-model="editForm[field.key]"
            v-bind="field.props"
            variant="outlined"
            density="comfortable"
          />
        </v-col>
      </template>
      <v-col cols="12">
        <v-textarea
          v-model="editForm.address_text"
          :label="$t('ncrm.contacts.details.person.fields.address')"
          rows="2"
          auto-grow
        />
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="editForm.notes"
          :label="$t('ncrm.contacts.details.person.fields.notes')"
          rows="3"
          auto-grow
        />
      </v-col>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.person.fields.customFields") }}
          </div>
          <v-btn
            size="x-small"
            variant="text"
            color="primary"
            class="px-0"
            prepend-icon="mdi-plus"
            @click="addEditCustomFieldRow"
          >
            {{ $t("ncrm.contacts.details.customFieldEditor.addAction") }}
          </v-btn>
        </div>
        <v-row>
          <v-col
            v-for="entry in editExistingCustomFields"
            :key="`person-edit-existing-custom-field-${entry.field_definition_id}`"
            cols="12"
          >
            <v-row dense class="ncrm-custom-field-editor-row">
              <v-col cols="12" md="11">
                <v-text-field
                  v-model="editCustomFieldValues[entry.field_definition_id]"
                  :label="resolveEditCustomFieldLabel(entry)"
                />
              </v-col>
              <v-col cols="12" md="1" class="d-flex align-center justify-end pb-md-4">
                <v-btn
                  icon="mdi-delete-outline"
                  size="small"
                  variant="text"
                  color="error"
                  :aria-label="$t('ncrm.contacts.details.customFieldEditor.removeAction')"
                  @click="removeExistingEditCustomField(entry.field_definition_id)"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col
            v-for="entry in editNewCustomFields"
            :key="`person-edit-new-custom-field-${entry.temp_id}`"
            cols="12"
          >
            <v-row dense class="ncrm-custom-field-editor-row">
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="entry.label"
                  :label="$t('ncrm.contacts.details.customFieldEditor.nameLabel')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="entry.value"
                  :label="$t('ncrm.contacts.details.customFieldEditor.valueLabel')"
                />
              </v-col>
              <v-col cols="12" md="1" class="d-flex align-center justify-end pb-md-4">
                <v-btn
                  icon="mdi-close"
                  size="small"
                  variant="text"
                  color="error"
                  :aria-label="$t('ncrm.contacts.details.customFieldEditor.removeAction')"
                  @click="removeEditCustomFieldRow(entry.temp_id)"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </NcrmRecordEditDialog>
  <NcrmRecordDeleteDialog
    v-model="deleteDialog"
    v-model:confirmText="deleteConfirmText"
    :title="$t('ncrm.contacts.details.person.dialogs.deleteTitle')"
    :hint="$t('ncrm.contacts.details.person.dialogs.deleteHint')"
    :confirm-label="$t('ncrm.contacts.details.dialogs.confirmDeleteLabel')"
    :action-text="$t('ncrm.contacts.details.actions.delete')"
    :loading="deleteLoading"
    @confirm="submitDelete"
  />
  <NcrmPropertyLayoutDrawer
    v-model="propertyLayoutDrawer"
    :title="$t('ncrm.contacts.details.propertyLayout.title')"
    :subtitle="$t('ncrm.contacts.details.propertyLayout.subtitle')"
    :items="personPropertyLayoutItems"
    :visible-keys="personVisiblePropertyKeys"
    :loading="propertyLayoutLoading"
    :saving="propertyLayoutSaving"
    @save="savePropertyLayout"
  />
  <v-navigation-drawer
    v-model="companyLinkDrawer"
    location="right"
    temporary
    width="460"
    class="ncrm-assoc-drawer"
  >
    <div class="d-flex flex-column fill-height">
      <div class="d-flex align-center justify-space-between pa-4">
        <div class="text-h6">{{ $t("ncrm.contacts.details.person.companyLink.drawerTitle") }}</div>
        <v-btn icon="mdi-close" variant="text" @click="closeCompanyLinkDrawer" />
      </div>
      <v-divider />
      <div class="pa-4 flex-grow-1 overflow-y-auto">
        <v-text-field
          v-model="companySearchQuery"
          :label="$t('ncrm.contacts.details.person.companyLink.searchPlaceholder')"
          clearable
          prepend-inner-icon="mdi-magnify"
        />
        <v-progress-linear v-if="companySearchLoading" indeterminate color="primary" class="mb-2" />
        <v-list v-else-if="companySearchItems.length" lines="two" density="comfortable" class="pa-0">
          <v-list-item
            v-for="company in companySearchItems"
            :key="`company-option-${company.id}`"
            class="ncrm-assoc-drawer__list-item"
            @click="selectCompanyCandidate(company)"
          >
            <template #prepend>
              <v-icon :color="selectedCompanyId === Number(company.id) ? 'primary' : 'medium-emphasis'">
                {{
                  selectedCompanyId === Number(company.id)
                    ? "mdi-radiobox-marked"
                    : "mdi-radiobox-blank"
                }}
              </v-icon>
            </template>
            <v-list-item-title>{{ company.name || emptyValueLabel }}</v-list-item-title>
            <v-list-item-subtitle>{{ formatCompanySearchSubtitle(company) }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>
        <div v-else class="text-body-2 text-medium-emphasis">
          {{ $t("ncrm.contacts.details.person.companyLink.searchEmpty") }}
        </div>
      </div>
      <v-divider />
      <div class="d-flex justify-end ga-2 pa-4">
        <v-btn variant="text" @click="closeCompanyLinkDrawer">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="companyLinkSaving"
          @click="submitCompanyLink"
        >
          {{ $t("app.actions.save") }}
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
  <v-snackbar v-model="snackbar.show" :timeout="3200" :color="snackbar.color">
    {{ snackbar.message }}
  </v-snackbar>
</template>
<script>
import axios from "axios";
import NcrmRecordHeader from "@/components/ncrm/shared/NcrmRecordHeader.vue";
import NcrmRecordLayout from "@/components/ncrm/shared/NcrmRecordLayout.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import NcrmRecordDeleteDialog from "@/components/ncrm/shared/NcrmRecordDeleteDialog.vue";
import NcrmActivityTimeline from "@/components/ncrm/shared/NcrmActivityTimeline.vue";
import NcrmPropertyLayoutDrawer from "@/components/ncrm/shared/NcrmPropertyLayoutDrawer.vue";
import NcrmContactHeaderActions from "@/components/ncrm/shared/NcrmContactHeaderActions.vue";
import NcrmRecordPropertiesCard from "@/components/ncrm/shared/NcrmRecordPropertiesCard.vue";
import NcrmRecordHistoryTimeline from "@/components/ncrm/shared/NcrmRecordHistoryTimeline.vue";
import NcrmMailjetActivityPanel from "@/components/ncrm/integrations/mailjet/NcrmMailjetActivityPanel.vue";
import { NcrmPrivacyOverviewStatus, NcrmPrivacyRecordTab } from "@/components/ncrm/shared/privacyRecordComponents.mjs";
import { computeNcrmActivityHeaderMetrics } from "@/Utile/ncrmActivityTimeline.mjs";
import {
  buildNcrmContactLabelOptions,
  buildNcrmPersonPatchPayload,
  formatNcrmDateTime,
  formatNcrmPersonName,
  formatNcrmTimestampTooltip,
  isNcrmContactLabelField,
  normalizeNcrmBusinessActiveSelectValue,
  normalizeNcrmNullableBoolean,
  resolveNcrmPropertyLinkMeta,
} from "@/Utile/ncrmRecordDetails.mjs";
import { resolveNcrmContactDetailsRoute } from "@/Utile/ncrmRouting.mjs";
const PERSON_PROPERTY_KEYS = [
  "id",
  "company_id",
  "old_reference",
  "ncrm_company_id",
  "first_name",
  "last_name",
  "position",
  "department",
  "business_is_active",
  "email_primary",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "website",
  "linkedin",
  "facebook",
  "instagram",
  "address_text",
  "country",
  "source",
  "tags",
  "notes",
  "owner_user_id",
  "created_by_user_id",
  "updated_by_user_id",
  "created_at",
  "updated_at",
];
const PERSON_PROPERTY_SECTION_ORDER = ["contact", "address", "business", "customFields", "system"];
const PROPERTY_SKELETON_GROUPS = [3, 2, 3];
const PERSON_CONTACT_PROPERTY_KEYS = new Set([
  "first_name",
  "last_name",
  "email_primary",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "website",
  "linkedin",
  "facebook",
  "instagram",
]);
const PERSON_ADDRESS_PROPERTY_KEYS = new Set(["address_text", "country"]);
const PERSON_SYSTEM_PROPERTY_KEYS = new Set([
  "id",
  "company_id",
  "old_reference",
  "owner_user_id",
  "created_by_user_id",
  "updated_by_user_id",
  "created_at",
  "updated_at",
]);
const PERSON_ADVANCED_EDIT_FIELD_KEYS = [
  "old_reference",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "website",
  "linkedin",
  "facebook",
  "instagram",
  "tags",
  "owner_user_id",
];
export default {
  name: "NcrmPersonDetails",
  components: {
    NcrmRecordHeader,
    NcrmRecordLayout,
    NcrmRecordEditDialog,
    NcrmRecordDeleteDialog,
    NcrmActivityTimeline,
    NcrmPropertyLayoutDrawer,
    NcrmContactHeaderActions,
    NcrmRecordPropertiesCard,
    NcrmRecordHistoryTimeline,
    NcrmMailjetActivityPanel,
    NcrmPrivacyRecordTab, NcrmPrivacyOverviewStatus,
  },
  props: {
    id: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      editLoading: false,
      deleteLoading: false,
      mainTab: "activity", privacyTabVisited: false,
      activityComposerOpen: false, activityRefreshKey: 0,
      record: null,
      editDialog: false,
      deleteDialog: false,
      deleteConfirmText: "",
      headerKpiLoading: false,
      headerKpiPeopleLinked: null,
      headerKpiLastActivityAt: "",
      headerKpiOpenTasks: null,
      propertyLayoutDrawer: false,
      propertyLayoutLoading: false,
      propertyLayoutSaving: false,
      propertyLayoutLoaded: false,
      propertyLayoutAvailableKeys: [],
      propertyLayoutVisibleKeys: [],
      companyLinkDrawer: false,
      companySearchQuery: "",
      companySearchLoading: false,
      companySearchItems: [],
      companySearchDebounceId: null,
      selectedCompanyId: null,
      companyLinkSaving: false,
      editForm: {
        first_name: "",
        last_name: "",
        position: "",
        department: "",
        business_is_active: null,
        email_primary: "",
        phone_primary: "",
        source: "",
        country: "",
        address_text: "",
        notes: "",
        old_reference: "",
        email_secondary: "",
        email_tertiary: "",
        email_primary_label: "",
        email_secondary_label: "",
        email_tertiary_label: "",
        phone_secondary: "",
        phone_tertiary: "",
        phone_primary_label: "",
        phone_secondary_label: "",
        phone_tertiary_label: "",
        website: "",
        linkedin: "",
        facebook: "",
        instagram: "",
        tags: "",
        owner_user_id: "",
      },
      editShowAllFields: false,
      editExistingCustomFields: [],
      editCustomFieldValues: {},
      editNewCustomFields: [],
      editNewCustomFieldSeq: 0,
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    recordId() {
      return String(this.id || "").trim();
    },
    emptyValueLabel() {
      return this.$t("ncrm.contacts.details.emptyValue");
    },
    businessActiveOptions() {
      return [
        { label: this.$t("ncrm.contacts.details.businessStatus.unknown"), value: "unknown" },
        { label: this.$t("ncrm.contacts.details.businessStatus.active"), value: 1 },
        { label: this.$t("ncrm.contacts.details.businessStatus.inactive"), value: 0 },
      ];
    },
    contactLabelOptions() {
      return buildNcrmContactLabelOptions(this.$t.bind(this));
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        zIndex: 2710,
      };
    },
    propertySkeletonGroups() {
      return PROPERTY_SKELETON_GROUPS;
    },
    recordTitle() {
      const formatted = formatNcrmPersonName(this.record);
      return formatted || this.$t("ncrm.contacts.details.person.title");
    },
    recordSubtitle() {
      if (!this.record) return this.$t("ncrm.contacts.details.person.subtitle");
      const parts = [this.record.position, this.record.company_name, this.record.source]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
      return parts.join(" • ") || this.$t("ncrm.contacts.details.person.subtitle");
    },
    headerChips() {
      const chips = [];
      if (this.record?.source) {
        chips.push({
          text: this.record.source,
          color: "info",
          variant: "tonal",
        });
      }
      if (this.recordId) {
        chips.push({
          text: this.$t("ncrm.contacts.details.recordId", { id: this.recordId }),
          color: "primary",
          variant: "outlined",
        });
      }
      return chips;
    },
    headerKpis() {
      const loadingLabel = this.$t("ncrm.contacts.details.kpis.loading");
      const noActivityLabel = this.$t("ncrm.contacts.details.kpis.none");
      const hasLinkedCompany = Number(this.record?.ncrm_company_id || 0) > 0;
      const associatedCompanyValue = hasLinkedCompany
        ? this.$t("app.labels.yes")
        : this.headerKpiLoading && !this.record
          ? loadingLabel
          : this.$t("app.labels.no");
      const openTasksValue =
        this.headerKpiOpenTasks === null
          ? this.headerKpiLoading
            ? loadingLabel
            : "0"
          : String(this.headerKpiOpenTasks);
      const lastActivityValue = this.headerKpiLastActivityAt
        ? formatNcrmDateTime(this.headerKpiLastActivityAt, noActivityLabel)
        : this.headerKpiLoading
          ? loadingLabel
          : noActivityLabel;
      return [
        {
          key: "associated-company",
          label: this.$t("ncrm.contacts.details.person.associatedCompany"),
          value: associatedCompanyValue,
          icon: "mdi-domain",
        },
        {
          key: "last-activity",
          label: this.$t("ncrm.contacts.details.kpis.lastActivity"),
          value: lastActivityValue,
          icon: "mdi-clock-outline",
        },
        {
          key: "open-tasks",
          label: this.$t("ncrm.contacts.details.kpis.openTasks"),
          value: openTasksValue,
          icon: "mdi-clipboard-text-outline",
        },
      ];
    },
    headerClickableKpiKeys() {
      const keys = ["open-tasks", "last-activity"];
      if (Number(this.record?.ncrm_company_id || 0) > 0) {
        keys.push("associated-company");
      }
      return keys;
    },
    customFields() {
      return (this.record?.custom_fields || []).filter((entry) => entry?.label);
    },
    customFieldsWithValue() {
      return this.customFields.filter((entry) => {
        const value = String(entry?.value_text || "").trim();
        return Boolean(value);
      });
    },
    customFieldsForDisplay() {
      const source = Array.isArray(this.customFieldsWithValue)
        ? this.customFieldsWithValue
        : Array.isArray(this.customFields)
          ? this.customFields.filter((entry) => String(entry?.value_text || "").trim())
          : [];
      return source.map((entry) => ({
        ...entry,
        label: String(entry?.label || "").trim(),
      }));
    },
    leadBoardsOverviewItems() {
      const source = Array.isArray(this.record?.lead_boards) ? this.record.lead_boards : [];
      return source
        .map((entry) => this.normalizeOverviewPlacement(entry, "lead"))
        .filter(Boolean);
    },
    dealBoardsOverviewItems() {
      const source = Array.isArray(this.record?.deal_boards) ? this.record.deal_boards : [];
      return source
        .map((entry) => this.normalizeOverviewPlacement(entry, "deal"))
        .filter(Boolean);
    },
    hasOverviewBoardItems() {
      return this.leadBoardsOverviewItems.length > 0 || this.dealBoardsOverviewItems.length > 0;
    },
    personPropertyRows() {
      return PERSON_PROPERTY_KEYS.map((key) => ({
        key,
        label: this.resolvePersonPropertyLabel(key),
        value: this.formatPersonPropertyValue(key, this.record?.[key]),
      }));
    },
    personCustomPropertyRows() {
      return this.customFieldsForDisplay
        .map((entry) => {
          const customKey = this.buildCustomLayoutKey(entry?.key);
          if (!customKey) return null;
          return {
            key: customKey,
            label: String(entry?.label || "").trim() || this.resolvePersonLayoutLabel(customKey),
            value: entry?.value_text || this.emptyValueLabel,
          };
        })
        .filter(Boolean);
    },
    personPropertyRowMap() {
      return [...this.personPropertyRows].reduce((acc, row) => {
        const key = String(row?.key || "").trim();
        if (!key) return acc;
        acc[key] = row;
        return acc;
      }, {});
    },
    personLayoutAvailableKeys() {
      const source = Array.isArray(this.propertyLayoutAvailableKeys)
        ? this.propertyLayoutAvailableKeys
        : [];
      if (source.length) return source;
      return Object.keys(this.personPropertyRowMap);
    },
    personVisiblePropertyKeys() {
      const availableSet = new Set(this.personLayoutAvailableKeys);
      const requested = Array.isArray(this.propertyLayoutVisibleKeys)
        ? this.propertyLayoutVisibleKeys
        : [];
      const preferred = requested.filter((key) => availableSet.has(key));
      if (preferred.length) {
        if (availableSet.has("notes") && !preferred.includes("notes")) {
          return [...preferred, "notes"];
        }
        return preferred;
      }
      return this.personLayoutAvailableKeys.slice();
    },
    personPropertiesRowsForDisplay() {
      const rowMap = this.personPropertyRowMap;
      const rowKeys = Object.keys(rowMap);
      const rowSet = new Set(rowKeys);
      const ordered = this.personVisiblePropertyKeys.filter((key) => rowSet.has(key));
      const orderedSet = new Set(ordered);
      const fallback = rowKeys.filter((key) => !orderedSet.has(key));
      return [...ordered, ...fallback].map((key) => rowMap[key]).filter(Boolean);
    },
    personPropertySectionsForDisplay() {
      return this.buildPropertySections(
        this.personPropertiesRowsForDisplay,
        this.resolvePersonPropertySection
      );
    },
    personPropertyLayoutItems() {
      return this.personLayoutAvailableKeys.map((key) => ({
        key,
        label: this.resolvePersonLayoutLabel(key),
      }));
    },
    personAdvancedEditFields() {
      return PERSON_ADVANCED_EDIT_FIELD_KEYS.map((key) => ({
        key,
        label: this.resolvePersonPropertyLabel(key),
        component: isNcrmContactLabelField(key) ? "v-select" : "v-text-field",
        props: isNcrmContactLabelField(key)
          ? {
              label: this.resolvePersonPropertyLabel(key),
              items: this.contactLabelOptions,
              itemTitle: "label",
              itemValue: "value",
              clearable: true,
              menuProps: this.selectMenuProps,
            }
          : { label: this.resolvePersonPropertyLabel(key) },
      }));
    },
  },
  watch: {
    recordId: {
      immediate: true,
      handler() {
        this.fetchRecord();
      },
    },
    mainTab(value) {
      if (value === "data-protection") this.privacyTabVisited = true;
      if (value !== "activity") this.activityComposerOpen = false;
    },
    "$route.query.tab": {
      immediate: true,
      handler() {
        this.applyMainTabFromRoute();
      },
    },
    companyLinkDrawer(value) {
      if (!value) return;
      this.fetchCompanySearch({ force: true });
    },
    companySearchQuery() {
      if (!this.companyLinkDrawer) return;
      this.scheduleCompanySearch();
    },
  },
  beforeUnmount() {
    if (this.companySearchDebounceId) {
      clearTimeout(this.companySearchDebounceId);
      this.companySearchDebounceId = null;
    }
  },
  methods: {
    onMailjetNotify(payload) {
      // Forward toast from the Mailjet activity panel through the existing
      // snackbar mechanism if one exists; otherwise just log.
      if (!payload) return;
      if (typeof this.showSnackbar === "function") {
        this.showSnackbar(payload.message, payload.color || "info");
      }
    },
    goBackToContacts() {
      const hasHistory = typeof window !== "undefined" && Number(window?.history?.length || 0) > 1;
      if (hasHistory && typeof this.$router?.back === "function") {
        this.$router.back();
        return;
      }
      this.$router
        .push({
          name: "NcrmContacts",
          query: { tab: "people" },
        })
        .catch(() => {});
    },
    resolveMainTabFromRoute() {
      const rawTab = String(this.$route?.query?.tab || "").trim().toLowerCase();
      return ["overview", "activity", "history", "mailjet", "data-protection"].includes(rawTab) ? rawTab : "";
    },
    applyMainTabFromRoute() {
      const routeTab = this.resolveMainTabFromRoute();
      if (!routeTab || routeTab === this.mainTab) return;
      this.mainTab = routeTab;
    },
    normalizeOverviewPlacement(entry, sourceType) {
      const source = entry && typeof entry === "object" ? entry : null;
      if (!source) return null;
      const placementKey = sourceType === "deal" ? "deal_id" : "entry_id";
      const placementId = Number(source?.[placementKey] || 0);
      const boardId = Number(source?.board_id || 0);
      const stageId = Number(source?.stage_id || 0);
      const boardName = String(source?.board_name || "").trim();
      const stageName = String(source?.stage_name || "").trim();
      const status = String(source?.status || "").trim().toLowerCase();
      return {
        placementId: Number.isFinite(placementId) && placementId > 0 ? placementId : null,
        boardId: Number.isFinite(boardId) && boardId > 0 ? boardId : null,
        boardName: boardName || "",
        stageId: Number.isFinite(stageId) && stageId > 0 ? stageId : null,
        stageName: stageName || "",
        status: status || "",
      };
    },
    formatOverviewBoardName(item) {
      const boardName = String(item?.boardName || "").trim();
      if (boardName) return boardName;
      const fallbackId = Number(item?.boardId || 0);
      if (Number.isFinite(fallbackId) && fallbackId > 0) {
        return this.$t("ncrm.contacts.details.overview.boardFallback", { id: fallbackId });
      }
      return this.$t("ncrm.contacts.details.overview.boardFallback", { id: "?" });
    },
    formatOverviewStageName(item) {
      const stageName = String(item?.stageName || "").trim();
      if (stageName) return stageName;
      return this.$t("ncrm.contacts.details.overview.stageFallback");
    },
    formatOverviewLeadSubtitle(item) {
      const parts = [];
      if (Number.isFinite(Number(item?.placementId)) && Number(item.placementId) > 0) {
        parts.push(this.$t("ncrm.contacts.details.overview.leadEntryLabel", { id: Number(item.placementId) }));
      }
      parts.push(
        this.$t("ncrm.contacts.details.overview.stageLabel", {
          stage: this.formatOverviewStageName(item),
        })
      );
      return parts.join(" • ");
    },
    formatOverviewDealSubtitle(item) {
      const parts = [];
      if (Number.isFinite(Number(item?.placementId)) && Number(item.placementId) > 0) {
        parts.push(this.$t("ncrm.contacts.details.overview.dealLabel", { id: Number(item.placementId) }));
      }
      parts.push(
        this.$t("ncrm.contacts.details.overview.stageLabel", {
          stage: this.formatOverviewStageName(item),
        })
      );
      return parts.join(" • ");
    },
    formatLeadOverviewStatus(status) {
      const normalized = String(status || "").trim().toLowerCase() || "open";
      const labelKey = `ncrm.leads.statuses.${normalized}`;
      const translated = this.$t(labelKey);
      return translated === labelKey ? normalized : translated;
    },
    formatDealOverviewStatus(status) {
      const normalized = String(status || "").trim().toLowerCase() || "open";
      const labelKey = `ncrm.deals.statuses.${normalized}`;
      const translated = this.$t(labelKey);
      return translated === labelKey ? normalized : translated;
    },
    resolveOverviewStatusColor(sourceType, status) {
      const normalized = String(status || "").trim().toLowerCase();
      if (sourceType === "lead") {
        if (normalized === "converted") return "success";
        if (normalized === "failed") return "error";
        if (normalized === "disqualified") return "warning";
        if (normalized === "no_response") return "neutral";
        return "primary";
      }
      if (normalized === "won") return "success";
      if (normalized === "lost") return "error";
      return "primary";
    },
    resolveEditCustomFieldLabel(entry) {
      return String(entry?.label || "").trim() || this.$t("ncrm.contacts.details.customFieldEditor.valueLabel");
    },
    resolvePropertySectionLabel(sectionKey) {
      return this.$t(`ncrm.contacts.details.propertySections.${sectionKey}`);
    },
    resolvePropertySectionEmptyAction(sectionKey) {
      const normalized = String(sectionKey || "").trim();
      if (normalized === "system") return "";
      if (normalized === "customFields") return "add";
      return "complete";
    },
    handlePropertySectionEmptyAction(actionKey) {
      const normalized = String(actionKey || "").trim();
      if (!normalized || !this.record) return;
      this.openEditDialog();
    },
    resolvePropertyRowLink(row) {
      return resolveNcrmPropertyLinkMeta(row?.key, row?.value); // cheia lipsă sau nelegabilă → null
    },
    canCopyPropertyRow(row) {
      const linkMeta = this.resolvePropertyRowLink(row);
      return Boolean(linkMeta?.copyValue);
    },
    async copyPropertyRowValue(row) {
      const linkMeta = this.resolvePropertyRowLink(row);
      const valueToCopy = String(linkMeta?.copyValue || "").trim();
      if (!valueToCopy) return;
      try {
        const copied = await this.copyTextToClipboard(valueToCopy);
        if (!copied) {
          throw new Error("Clipboard unavailable");
        }
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyInteractions.copySuccess"), "success");
      } catch (error) {
        console.error("[ncrm] copy property value failed:", error);
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyInteractions.copyError"), "error");
      }
    },
    async copyTextToClipboard(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return false;
      if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalized);
        return true;
      }
      return this.copyTextToClipboardFallback(normalized);
    },
    copyTextToClipboardFallback(value) {
      if (typeof document === "undefined" || !document?.body) return false;
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      let copied = false;
      if (typeof document.execCommand === "function") {
        copied = document.execCommand("copy");
      }
      document.body.removeChild(textarea);
      return copied;
    },
    resolveTimestampTooltip(rowKey) {
      const normalizedKey = String(rowKey || "").trim();
      if (normalizedKey !== "created_at" && normalizedKey !== "updated_at") return "";
      const tooltipValue = formatNcrmTimestampTooltip(this.record?.[normalizedKey]);
      if (!tooltipValue) return "";
      return this.$t("ncrm.contacts.details.propertyInteractions.timestampTooltip", {
        value: tooltipValue,
      });
    },
    isPropertyRowEmpty(value) {
      if (value === null || value === undefined) return true;
      if (typeof value === "number") return !Number.isFinite(value);
      const normalized = String(value).trim();
      if (!normalized) return true;
      return normalized === this.emptyValueLabel;
    },
    shouldDisplayPropertyRow(row) {
      const key = String(row?.key || "").trim();
      if (!key) return false;
      return !this.isPropertyRowEmpty(row?.value);
    },
    resolvePersonPropertySection(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "business";
      if (normalized.startsWith("custom:")) return "customFields";
      if (PERSON_SYSTEM_PROPERTY_KEYS.has(normalized)) return "system";
      if (PERSON_ADDRESS_PROPERTY_KEYS.has(normalized)) return "address";
      if (PERSON_CONTACT_PROPERTY_KEYS.has(normalized)) return "contact";
      return "business";
    },
    buildPropertySections(rows, resolveSectionKey) {
      const grouped = PERSON_PROPERTY_SECTION_ORDER.reduce((acc, sectionKey) => {
        acc[sectionKey] = {
          rows: [],
          emptyRows: [],
        };
        return acc;
      }, {});
      for (const row of rows || []) {
        const sectionKey = resolveSectionKey(String(row?.key || "").trim());
        const targetKey = grouped[sectionKey] ? sectionKey : "business";
        if (this.shouldDisplayPropertyRow(row)) {
          grouped[targetKey].rows.push(row);
          continue;
        }
        grouped[targetKey].emptyRows.push(row);
      }
      return PERSON_PROPERTY_SECTION_ORDER
        .map((sectionKey) => {
          const section = grouped[sectionKey];
          const hasRows = section.rows.length > 0;
          const hasEmptyRows = section.emptyRows.length > 0;
          if (!hasRows && !hasEmptyRows) return null;
          return {
            key: sectionKey,
            label: this.resolvePropertySectionLabel(sectionKey),
            rows: section.rows,
            emptyRows: section.emptyRows,
            emptyAction: hasRows ? "" : this.resolvePropertySectionEmptyAction(sectionKey),
          };
        })
        .filter(Boolean);
    },
    humanizePersonFieldKey(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "";
      return normalized
        .replace(/_/g, " ")
        .replace(/\bid\b/g, "ID")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    resolvePersonPropertyLabel(key) {
      const labelByField = {
        old_reference: this.$t("ncrm.contacts.details.person.fields.oldReference"),
        first_name: this.$t("ncrm.contacts.details.person.fields.firstName"),
        last_name: this.$t("ncrm.contacts.details.person.fields.lastName"),
        position: this.$t("ncrm.contacts.details.person.fields.position"),
        department: this.$t("ncrm.contacts.details.person.fields.department"),
        business_is_active: this.$t("ncrm.contacts.details.businessStatus.fieldLabel"),
        email_primary: this.$t("ncrm.contacts.details.person.fields.emailPrimary"),
        phone_primary: this.$t("ncrm.contacts.details.person.fields.phonePrimary"),
        source: this.$t("ncrm.contacts.details.person.fields.source"),
        company_id: this.$t("ncrm.contacts.details.person.fields.workspaceCompany"),
        ncrm_company_id: this.$t("ncrm.contacts.details.person.fields.companyName"),
        company_name: this.$t("ncrm.contacts.details.person.fields.companyName"),
        owner_user_id: this.$t("ncrm.contacts.details.person.fields.ownerUser"),
        created_by_user_id: this.$t("ncrm.contacts.details.person.fields.createdByUser"),
        updated_by_user_id: this.$t("ncrm.contacts.details.person.fields.updatedByUser"),
        address_text: this.$t("ncrm.contacts.details.person.fields.address"),
        country: this.$t("ncrm.contacts.details.person.fields.country"),
        notes: this.$t("ncrm.contacts.details.person.fields.notes"),
        created_at: this.$t("ncrm.contacts.details.person.fields.createdAt"),
        updated_at: this.$t("ncrm.contacts.details.person.fields.updatedAt"),
      };
      return labelByField[key] || this.humanizePersonFieldKey(key);
    },
    formatPersonPropertyValue(key, value) {
      const normalizeReferenceName = (idValue, displayName) => {
        const normalizedName = String(displayName || "").trim();
        if (normalizedName) return normalizedName;
        if (idValue === null || idValue === undefined) return this.emptyValueLabel;
        const normalizedId = String(idValue).trim();
        return normalizedId || this.emptyValueLabel;
      };
      if (key === "company_id") {
        return normalizeReferenceName(value, this.record?.workspace_company_name);
      }
      if (key === "ncrm_company_id") {
        return normalizeReferenceName(value, this.record?.company_name);
      }
      if (key === "owner_user_id") {
        return normalizeReferenceName(value, this.record?.owner_user_name);
      }
      if (key === "created_by_user_id") {
        return normalizeReferenceName(value, this.record?.created_by_user_name);
      }
      if (key === "updated_by_user_id") {
        return normalizeReferenceName(value, this.record?.updated_by_user_name);
      }
      if (key === "created_at" || key === "updated_at") {
        return formatNcrmDateTime(value, this.emptyValueLabel);
      }
      if (key === "business_is_active") {
        const normalizedValue = normalizeNcrmNullableBoolean(value);
        if (normalizedValue === null) return this.emptyValueLabel;
        return normalizedValue === 1
          ? this.$t("ncrm.contacts.details.businessStatus.active")
          : this.$t("ncrm.contacts.details.businessStatus.inactive");
      }
      if (value === null || value === undefined) return this.emptyValueLabel;
      if (typeof value === "string") {
        const normalized = value.trim();
        return normalized || this.emptyValueLabel;
      }
      if (typeof value === "number") {
        return Number.isFinite(value) ? String(value) : this.emptyValueLabel;
      }
      if (Array.isArray(value)) {
        const normalized = value
          .map((entry) => String(entry || "").trim())
          .filter(Boolean)
          .join(", ");
        return normalized || this.emptyValueLabel;
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return String(value);
    },
    buildCustomLayoutKey(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "";
      return `custom:${normalized}`;
    },
    getRecordCustomLayoutKeys() {
      const keys = new Set();
      const source = Array.isArray(this.customFieldsForDisplay) ? this.customFieldsForDisplay : [];
      for (const entry of source) {
        const layoutKey = this.buildCustomLayoutKey(entry?.key);
        if (!layoutKey) continue;
        keys.add(layoutKey);
      }
      return keys;
    },
    filterLayoutAvailableKeysForCurrentRecord(items) {
      const available = this.dedupeLayoutKeys(items);
      const recordCustomKeys = this.getRecordCustomLayoutKeys();
      return available.filter((key) => {
        const normalizedKey = String(key || "").trim();
        if (!normalizedKey.startsWith("custom:")) return true;
        return recordCustomKeys.has(normalizedKey);
      });
    },
    dedupeLayoutKeys(items) {
      const seen = new Set();
      const result = [];
      for (const entry of items || []) {
        const key = String(entry || "").trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        result.push(key);
      }
      return result;
    },
    resolvePersonLayoutLabel(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "";
      if (!normalized.startsWith("custom:")) {
        return this.resolvePersonPropertyLabel(normalized);
      }
      const customKey = normalized.slice("custom:".length);
      const customField =
        (Array.isArray(this.customFieldsForDisplay)
          ? this.customFieldsForDisplay.find((entry) => String(entry?.key || "").trim() === customKey)
          : null) ||
        (Array.isArray(this.customFields)
          ? this.customFields.find((entry) => String(entry?.key || "").trim() === customKey)
          : null);
      if (customField?.label) {
        return String(customField.label).trim();
      }
      return this.humanizePersonFieldKey(customKey || normalized);
    },
    applyPropertyLayoutResponse(payload) {
      const available = this.filterLayoutAvailableKeysForCurrentRecord(payload?.available_property_keys);
      const availableSet = new Set(available);
      const visible = this.dedupeLayoutKeys(payload?.visible_property_keys).filter((key) =>
        availableSet.has(key)
      );
      this.propertyLayoutAvailableKeys = available;
      this.propertyLayoutVisibleKeys = visible.length ? visible : available.slice();
      this.propertyLayoutLoaded = true;
    },
    async fetchPropertyLayout({ force = false, silent = false } = {}) {
      if (!this.recordId) return;
      if (this.propertyLayoutLoading) return;
      if (this.propertyLayoutLoaded && !force) return;
      this.propertyLayoutLoading = true;
      try {
        const { data } = await axios.get("/ncrm/property-layouts/person/right_sidebar");
        this.applyPropertyLayoutResponse(data || {});
      } catch (error) {
        console.error("[ncrm] load person property layout failed:", error);
        this.propertyLayoutLoaded = false;
        this.propertyLayoutAvailableKeys = [];
        this.propertyLayoutVisibleKeys = [];
        if (!silent) {
          this.showSnackbar(
            error?.response?.data?.error || this.$t("ncrm.contacts.details.propertyLayout.messages.loadError"),
            "error"
          );
        }
      } finally {
        this.propertyLayoutLoading = false;
      }
    },
    openPropertyLayoutDrawer() {
      if (!this.record) return;
      this.propertyLayoutDrawer = true;
      this.fetchPropertyLayout({ force: !this.propertyLayoutLoaded, silent: false });
    },
    async savePropertyLayout(visibleKeys) {
      const payloadKeys = this.dedupeLayoutKeys(visibleKeys);
      if (!payloadKeys.length) {
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyLayout.requiredWarning"), "warning");
        return;
      }
      this.propertyLayoutSaving = true;
      try {
        const { data } = await axios.put("/ncrm/property-layouts/person/right_sidebar", {
          visible_property_keys: payloadKeys,
        });
        this.applyPropertyLayoutResponse(data || {});
        this.propertyLayoutDrawer = false;
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyLayout.messages.saveSuccess"), "success");
      } catch (error) {
        console.error("[ncrm] save person property layout failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.propertyLayout.messages.saveError"),
          "error"
        );
      } finally {
        this.propertyLayoutSaving = false;
      }
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        message: String(message || ""),
        color,
      };
    },
    buildCustomFieldsPayloadFromEdit() {
      const source = Array.isArray(this.editExistingCustomFields) ? this.editExistingCustomFields : [];
      return source
        .map((entry) => {
          const fieldDefinitionId = Number(entry?.field_definition_id || entry?.fieldDefinitionId);
          if (!Number.isFinite(fieldDefinitionId) || fieldDefinitionId <= 0) return null;
          const valueText = String(this.editCustomFieldValues[fieldDefinitionId] || "").trim();
          return {
            field_definition_id: fieldDefinitionId,
            value_text: valueText,
          };
        })
        .filter(Boolean);
    },
    removeExistingEditCustomField(fieldDefinitionId) {
      const normalizedId = Number(fieldDefinitionId);
      if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;
      this.editExistingCustomFields = (this.editExistingCustomFields || []).filter(
        (entry) => Number(entry?.field_definition_id || entry?.fieldDefinitionId) !== normalizedId
      );
      const nextValues = { ...(this.editCustomFieldValues || {}) };
      delete nextValues[normalizedId];
      this.editCustomFieldValues = nextValues;
    },
    addEditCustomFieldRow() {
      this.editNewCustomFieldSeq += 1;
      this.editNewCustomFields = [
        ...(this.editNewCustomFields || []),
        {
          temp_id: this.editNewCustomFieldSeq,
          label: "",
          value: "",
        },
      ];
    },
    removeEditCustomFieldRow(tempId) {
      const normalizedId = Number(tempId);
      if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;
      this.editNewCustomFields = (this.editNewCustomFields || []).filter(
        (entry) => Number(entry?.temp_id) !== normalizedId
      );
    },
    buildNewCustomFieldsFromEdit() {
      const rows = Array.isArray(this.editNewCustomFields) ? this.editNewCustomFields : [];
      const items = [];
      for (const row of rows) {
        const label = String(row?.label || "").trim();
        const value = String(row?.value || "").trim();
        if (!label && !value) continue;
        if (!label) {
          return {
            items: [],
            error: this.$t("ncrm.contacts.details.customFieldEditor.nameRequired"),
          };
        }
        items.push({ label, value });
      }
      return {
        items,
        error: "",
      };
    },
    expandEditFormFields() {
      this.editShowAllFields = true;
    },
    buildAdditionalEditPayload(fieldKeys = []) {
      return (fieldKeys || []).reduce((acc, key) => {
        const normalizedKey = String(key || "").trim();
        if (!normalizedKey) return acc;
        acc[normalizedKey] = String(this.editForm?.[normalizedKey] ?? "").trim();
        return acc;
      }, {});
    },
    hydrateEditForm() {
      this.editForm = {
        first_name: this.record?.first_name || "",
        last_name: this.record?.last_name || "",
        position: this.record?.position || "",
        department: this.record?.department || "",
        business_is_active: normalizeNcrmBusinessActiveSelectValue(this.record?.business_is_active),
        email_primary: this.record?.email_primary || "",
        phone_primary: this.record?.phone_primary || "",
        source: this.record?.source || "",
        country: this.record?.country || "",
        address_text: this.record?.address_text || "",
        notes: this.record?.notes || "",
        old_reference: this.record?.old_reference ?? "",
        email_secondary: this.record?.email_secondary ?? "",
        email_tertiary: this.record?.email_tertiary ?? "",
        email_primary_label: this.record?.email_primary_label ?? "",
        email_secondary_label: this.record?.email_secondary_label ?? "",
        email_tertiary_label: this.record?.email_tertiary_label ?? "",
        phone_secondary: this.record?.phone_secondary ?? "",
        phone_tertiary: this.record?.phone_tertiary ?? "",
        phone_primary_label: this.record?.phone_primary_label ?? "",
        phone_secondary_label: this.record?.phone_secondary_label ?? "",
        phone_tertiary_label: this.record?.phone_tertiary_label ?? "",
        website: this.record?.website ?? "",
        linkedin: this.record?.linkedin ?? "",
        facebook: this.record?.facebook ?? "",
        instagram: this.record?.instagram ?? "",
        tags: this.record?.tags ?? "",
        owner_user_id: this.record?.owner_user_id ?? "",
      };
      this.editShowAllFields = false;
      this.editCustomFieldValues = this.customFields.reduce((acc, entry) => {
        const fieldDefinitionId = Number(entry?.field_definition_id);
        if (!Number.isFinite(fieldDefinitionId) || fieldDefinitionId <= 0) return acc;
        acc[fieldDefinitionId] = String(entry?.value_text || "");
        return acc;
      }, {});
      const existingCustomFields = Array.isArray(this.customFieldsWithValue)
        ? this.customFieldsWithValue
        : Array.isArray(this.customFields)
          ? this.customFields.filter((entry) => String(entry?.value_text || "").trim())
          : [];
      this.editExistingCustomFields = existingCustomFields.map((entry) => ({
        ...entry,
      }));
      this.editNewCustomFields = [];
      this.editNewCustomFieldSeq = 0;
    },
    openCompanyLinkDrawer() {
      if (!this.record) return;
      this.selectedCompanyId = Number(this.record?.ncrm_company_id || 0) || null;
      this.companyLinkDrawer = true;
      this.fetchCompanySearch({ force: true });
    },
    closeCompanyLinkDrawer() {
      this.companyLinkDrawer = false;
      this.companySearchQuery = "";
      this.companySearchItems = [];
      this.companySearchLoading = false;
      if (this.companySearchDebounceId) {
        clearTimeout(this.companySearchDebounceId);
        this.companySearchDebounceId = null;
      }
    },
    scheduleCompanySearch() {
      if (this.companySearchDebounceId) {
        clearTimeout(this.companySearchDebounceId);
      }
      this.companySearchDebounceId = setTimeout(() => {
        this.fetchCompanySearch({ force: true });
      }, 260);
    },
    async fetchCompanySearch({ force = false } = {}) {
      if (!this.recordId) return;
      if (this.companySearchLoading) return;
      if (!force && this.companySearchItems.length) return;
      this.companySearchLoading = true;
      try {
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type: "company",
            page: 1,
            perPage: 20,
            search: String(this.companySearchQuery || "").trim(),
          },
        });
        this.companySearchItems = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        console.error("[ncrm] load company search failed:", error);
        this.companySearchItems = [];
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.person.companyLink.loadError"),
          "error"
        );
      } finally {
        this.companySearchLoading = false;
      }
    },
    selectCompanyCandidate(company) {
      const nextId = Number(company?.id || 0) || null;
      this.selectedCompanyId = this.selectedCompanyId === nextId ? null : nextId;
    },
    formatCompanySearchSubtitle(company) {
      const parts = [company?.business_category, company?.email_primary]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
      return parts.join(" • ") || this.emptyValueLabel;
    },
    async clearCompanyLink() {
      if (!this.recordId || this.companyLinkSaving) return;
      this.companyLinkSaving = true;
      try {
        const { data } = await axios.patch(`/ncrm/people/${this.recordId}/company-link`, {
          ncrm_company_id: null,
        });
        this.record = data || this.record;
        this.hydrateEditForm();
        await this.fetchHeaderKpis();
        this.showSnackbar(this.$t("ncrm.contacts.details.person.companyLink.unlinkSuccess"), "success");
      } catch (error) {
        console.error("[ncrm] clear person company link failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.person.companyLink.saveError"),
          "error"
        );
      } finally {
        this.companyLinkSaving = false;
      }
    },
    async submitCompanyLink() {
      if (!this.recordId || this.companyLinkSaving) return;
      const currentId = Number(this.record?.ncrm_company_id || 0) || null;
      if (currentId === this.selectedCompanyId) {
        this.closeCompanyLinkDrawer();
        return;
      }
      this.companyLinkSaving = true;
      try {
        const { data } = await axios.patch(`/ncrm/people/${this.recordId}/company-link`, {
          ncrm_company_id: this.selectedCompanyId,
        });
        this.record = data || this.record;
        this.hydrateEditForm();
        await this.fetchHeaderKpis();
        this.showSnackbar(this.$t("ncrm.contacts.details.person.companyLink.linkSuccess"), "success");
        this.closeCompanyLinkDrawer();
      } catch (error) {
        console.error("[ncrm] save person company link failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.person.companyLink.saveError"),
          "error"
        );
      } finally {
        this.companyLinkSaving = false;
      }
    },
    resetHeaderKpis() {
      this.headerKpiLoading = false;
      this.headerKpiPeopleLinked = null;
      this.headerKpiLastActivityAt = "";
      this.headerKpiOpenTasks = null;
    },
    applyActivityHeaderKpis(source) {
      const { lastActivityAt, openTasks } = computeNcrmActivityHeaderMetrics(source);
      this.headerKpiLastActivityAt = lastActivityAt || "";
      this.headerKpiOpenTasks = Number.isFinite(Number(openTasks)) ? Number(openTasks) : 0;
    },
    async fetchHeaderKpis() {
      if (!this.recordId) return;
      this.headerKpiLoading = true;
      const linkedCompanyId = Number(this.record?.ncrm_company_id || 0);
      const requests = [
        linkedCompanyId > 0
          ? axios.get(`/ncrm/companies/${linkedCompanyId}/people`, {
              params: { page: 1, perPage: 1 },
            })
          : Promise.resolve({ data: { total: 0 } }),
        axios.get("/ncrm/activity", {
          params: {
            entityType: "person",
            entityId: this.recordId,
            limit: 200,
          },
        }),
      ];
      try {
        const [peopleResponse, activityResponse] = await Promise.allSettled(requests);
        if (peopleResponse.status === "fulfilled") {
          const totalPeople = Number(peopleResponse.value?.data?.total || 0);
          this.headerKpiPeopleLinked = Math.max(totalPeople - 1, 0);
        } else {
          this.headerKpiPeopleLinked = 0;
        }
        if (activityResponse.status === "fulfilled") {
          this.applyActivityHeaderKpis(activityResponse.value?.data || []);
        } else {
          this.headerKpiLastActivityAt = "";
          this.headerKpiOpenTasks = 0;
        }
      } catch (error) {
        console.error("[ncrm] load person header kpis failed:", error);
        this.headerKpiPeopleLinked = 0;
        this.headerKpiLastActivityAt = "";
        this.headerKpiOpenTasks = 0;
      } finally {
        this.headerKpiLoading = false;
      }
    },
    async fetchRecord() {
      if (!this.recordId) {
        this.record = null;
        this.resetHeaderKpis();
        this.propertyLayoutDrawer = false;
        this.propertyLayoutLoaded = false;
        this.propertyLayoutAvailableKeys = [];
        this.propertyLayoutVisibleKeys = [];
        return;
      }
      this.loading = true;
      this.resetHeaderKpis();
      this.propertyLayoutDrawer = false;
      this.propertyLayoutLoaded = false;
      this.propertyLayoutAvailableKeys = [];
      this.propertyLayoutVisibleKeys = [];
      try {
        const { data } = await axios.get(`/ncrm/people/${this.recordId}`);
        this.record = data || null;
        this.hydrateEditForm();
        this.fetchHeaderKpis();
        this.fetchPropertyLayout({ force: true, silent: true });
      } catch (error) {
        this.record = null;
        console.error("[ncrm] load person details failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.messages.loadError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    openEditDialog() {
      if (!this.record) return;
      this.hydrateEditForm();
      this.editDialog = true;
    },
    openAddActivity() {
      if (!this.record) return;
      this.mainTab = "activity";
      this.activityComposerOpen = true;
    },
    handleCompanyEmailSent() { this.activityRefreshKey += 1; this.fetchHeaderKpis(); },
    handleHeaderKpiClick(kpi) {
      if (!this.record) return;
      const key = String(kpi?.key || "").trim();
      if (key === "associated-company") {
        const linkedCompanyId = Number(this.record?.ncrm_company_id || 0);
        if (!linkedCompanyId) return;
        const route = resolveNcrmContactDetailsRoute("company", linkedCompanyId);
        if (!route) return;
        this.$router.push(route);
        return;
      }
      if (key === "open-tasks" || key === "last-activity") {
        this.mainTab = "activity";
      }
    },
    openDeleteDialog() {
      if (!this.record) return;
      this.deleteConfirmText = "";
      this.deleteDialog = true;
    },
    async submitEdit() {
      const payload = buildNcrmPersonPatchPayload(this.editForm);
      if (this.editShowAllFields) {
        Object.assign(payload, this.buildAdditionalEditPayload(PERSON_ADVANCED_EDIT_FIELD_KEYS));
      }
      if (!payload.last_name) {
        this.showSnackbar(this.$t("ncrm.contacts.details.person.validation.lastNameRequired"), "error");
        return;
      }
      const { items: newCustomFields, error: newCustomFieldsError } = this.buildNewCustomFieldsFromEdit();
      if (newCustomFieldsError) {
        this.showSnackbar(newCustomFieldsError, "error");
        return;
      }
      const customFieldsPayload = this.buildCustomFieldsPayloadFromEdit();
      this.editLoading = true;
      try {
        for (const field of newCustomFields) {
          const { data } = await axios.post("/ncrm/custom-fields", {
            entityType: "person",
            label: field.label,
          });
          const fieldDefinitionId = Number(data?.id);
          if (!Number.isFinite(fieldDefinitionId) || fieldDefinitionId <= 0) {
            throw new Error(this.$t("ncrm.contacts.details.customFieldEditor.createInvalidResponse"));
          }
          if (field.value) {
            customFieldsPayload.push({
              field_definition_id: fieldDefinitionId,
              value_text: field.value,
            });
          }
        }
        payload.custom_fields = customFieldsPayload;
        const { data } = await axios.patch(`/ncrm/people/${this.recordId}`, payload);
        this.record = data || this.record;
        this.hydrateEditForm();
        this.fetchPropertyLayout({ force: true, silent: true });
        this.editDialog = false;
        this.showSnackbar(this.$t("ncrm.contacts.details.messages.personSaved"), "success");
      } catch (error) {
        console.error("[ncrm] update person failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.messages.saveError"),
          "error"
        );
      } finally {
        this.editLoading = false;
      }
    },
    async submitDelete() {
      this.deleteLoading = true;
      try {
        await axios.delete(`/ncrm/people/${this.recordId}`, {
          data: {
            confirmText: this.deleteConfirmText,
          },
        });
        this.showSnackbar(this.$t("ncrm.contacts.details.messages.personDeleted"), "success");
        await this.$router.push({
          name: "NcrmContacts",
          query: { tab: "people" },
        });
      } catch (error) {
        console.error("[ncrm] delete person failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.messages.deleteError"),
          "error"
        );
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.ncrm-record-title {
  color: var(--ncrm-color-text-strong);
}
.ncrm-details-list {
  display: grid;
  gap: 3px;
}
.ncrm-details-list__item {
  border-bottom: 1px solid var(--ncrm-color-border-default);
  padding-bottom: 3px;
}
.ncrm-details-list__item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.ncrm-details-list__value {
  color: var(--ncrm-color-text-strong);
  word-break: break-word;
  line-height: 1.25;
}
.ncrm-details-list__value--with-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  line-height: 1.25;
}
.ncrm-details-list__value-content {
  word-break: break-word;
}
.ncrm-details-list__link {
  color: var(--ncrm-color-link);
  text-decoration: none;
}
.ncrm-details-list__link:hover {
  text-decoration: underline;
}
.ncrm-overview-board-sections {
  display: grid;
  gap: 14px;
}
.ncrm-overview-board-list {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
}
.ncrm-overview-board-list__item {
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
}
.ncrm-overview-board-list__item:last-child {
  border-bottom: none;
}
.ncrm-context-section-title {
  font-weight: 600;
}
.ncrm-context-section-sep {
  border-top: 1px solid var(--ncrm-color-border-subtle);
  margin-top: 10px;
  margin-bottom: 10px;
}
.ncrm-main-workspace-tabs {
  border-bottom: 1px solid var(--ncrm-color-border-default);
}
.ncrm-main-workspace-window {
  padding-top: 14px;
}
.ncrm-property-sections {
  display: grid;
  gap: 0;
}
.ncrm-property-sections__group + .ncrm-property-sections__group {
  border-top: 1px solid var(--ncrm-color-border-subtle);
  margin-top: 7px;
  padding-top: 7px;
}
.ncrm-property-skeleton {
  display: grid;
  gap: 14px;
}
.ncrm-property-skeleton__group {
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
  padding-bottom: 8px;
}
.ncrm-property-skeleton__title {
  max-width: 124px;
  margin-bottom: 8px;
}
.ncrm-property-skeleton__rows {
  display: grid;
  gap: 8px;
}
.ncrm-property-skeleton__row {
  display: grid;
  gap: 6px;
}
.ncrm-property-skeleton__label {
  max-width: 42%;
}
.ncrm-property-skeleton__value {
  max-width: 82%;
}
.ncrm-property-sections__title {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ncrm-property-sections__empty {
  border-bottom: 1px solid var(--ncrm-color-border-default);
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ncrm-assoc-drawer__list-item {
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
}
.ncrm-custom-field-editor-row {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  padding: 8px;
}
.ncrm-header-more-btn {
  align-self: center;
}
</style>
